import { PropsWithChildren, useEffect } from "react";
import { useAppDispatch } from "./redux/hooks";
import {
  AnalyticsService,
  AnalyticsTrackingEvent,
  BrandclubStoreConfigUtils,
  IAppInstallationInfo,
  buildAppInstallationInfo,
  useFingerprintJSContext,
} from "@brandclub/common-ui";
import { updateAppInstallationInfoInCheckoutBar } from "./api/rest/user";
import { HOST_DOMAIN } from "./utils/domain";
import { updateVisitorTracking } from "./redux/reducers/visitorTracking";
import { removeFunctionFields } from "./utils";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  appConfigQueryOptions,
  useBrandAppConfigWithFallback,
} from "./api/cache/appConfigQueryOptions";
import { appThemeQueryOptions } from "./api/cache/appThemeQueryOptions";
import {
  brandclubProductQueryOptions,
  useCurrentBrandclubProduct,
  useDtcSiteProduct,
} from "./api/cache/dtcProductQueryOptions";
import {
  userProfileQueryOptions,
  useUserProfile,
} from "./api/cache/userProfileQueryOptions";
import { usePersistedQueryManager } from "./api/cache/PersistedQueryManager";
import {
  activeCartsQueryOptions,
  useCartCount,
} from "./api/cache/cartQueryOptions";
import { useTrackViewEvent, useTrackActions } from "./hooks/useTracking";
import { useCurrentStacklineSku } from "./hooks/useCurrentStacklineSku";

const initAppInstallationInfo = async (
  userId: string | undefined,
  signal?: AbortSignal
) => {
  if (!userId) {
    return;
  }
  try {
    const appId = BrandclubStoreConfigUtils.getWebAppId();
    const appInstallationInfo: IAppInstallationInfo =
      await buildAppInstallationInfo({
        userId,
        appId,
      });
    if (appInstallationInfo) {
      await updateAppInstallationInfoInCheckoutBar(appInstallationInfo, signal);
    }
  } catch (e) {
    console.error("An error occurred when building appInstallationInfo: ", e);
  }
};

// created as an isolated component to prevent changes in tracking from affecting rendering of the app.
const MixpanelTracking: React.FC<{ domain: string }> = ({ domain }) => {
  const dispatch = useAppDispatch();
  const visitorTracking = useFingerprintJSContext();
  const currentStacklineSku = useCurrentStacklineSku();
  const { data: cartCount } = useCartCount();
  const { data: userProfile, isLoading: isUserProfileLoading } =
    useUserProfile();
  const isLoggedIn = !!userProfile;
  // Get product data from cache without triggering a fetch
  const { data: product } = useQuery({
    ...brandclubProductQueryOptions(currentStacklineSku),
    enabled: false, // Prevents fetching
    staleTime: Infinity, // Prevents background refetches
  });
  // Get checkout bar treatments from cache without triggering a fetch
  const { data: checkoutBarTreatments } = useQuery({
    ...appConfigQueryOptions(),
    enabled: false,
    staleTime: Infinity,
    select: (data) => data.domainConfig?.abTestingTreatmentResults,
  });

  const trackPageView = useTrackViewEvent(AnalyticsTrackingEvent.PAGE_VIEW);
  const [_, appendCheckoutBarActionDetails] = useTrackActions();

  useEffect(() => {
    if (!product) {
      return;
    }

    const checkoutBarVisible =
      // if treatment is off, don't show the bar
      checkoutBarTreatments?.["brandclub_dtc_checkout_bar"]?.treatment !==
        "off" &&
      // AND if the it is a DTC product page or there are items in the cart show the bar
      (currentStacklineSku || !!cartCount);
    const productInfo = {
      pageId: product.stacklineSku,
      retailerId: product.retailerId,
      retailPrice: product.retailPrice,
      retailerSku: product.retailerSku,
      stacklineSku: product.stacklineSku,
      totalRewardAmount: product.totalRewardAmount,
      retailerProductPageUrl: product.retailerProductPageUrl,
      title: product.title,
      checkoutEligibility: product.checkoutEligibility,
      matchedProducts: product.matchedProducts,
    };
    trackPageView(
      {
        ...productInfo,
        checkoutBarVisible,
        checkoutBarTreatments,
        isAuthenticated: isLoggedIn,
      },
      {}
    );
    appendCheckoutBarActionDetails({
      ...productInfo,
      isAuthenticated: isLoggedIn,
    });
  }, [currentStacklineSku, isLoggedIn, product]);

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    try {
      if (!AnalyticsService.isMixpanelInitialized()) {
        AnalyticsService.initializeMixpanel(domain);
      }
      AnalyticsService.setCurrentVisitorTracking(visitorTracking);
      if (isUserProfileLoading) {
        return;
      }
      AnalyticsService.setMixpanelUserIdentity(visitorTracking, userProfile);
      if (visitorTracking?.uniqueVisitorId) {
        dispatch(updateVisitorTracking(removeFunctionFields(visitorTracking)));
      }
      initAppInstallationInfo(userProfile?.userId, signal).catch((e) => {
        if (e instanceof Error && e.name === "TaskAbortError") {
          // ignore abort errors
          return;
        }
        console.warn("Error initializing app installation info", e);
      });
    } catch (e) {
      console.warn(e);
    }

    return () => {
      controller.abort("retrying updateAppInstallationInfo");
    };
  }, [dispatch, isUserProfileLoading, visitorTracking, userProfile]);

  return null;
};

export const AppInitializer: React.FC<PropsWithChildren> = ({ children }) => {
  const queryClient = useQueryClient();
  const persistedQueryManager = usePersistedQueryManager();
  // prefetch brand app config
  useBrandAppConfigWithFallback({
    notifyOnChangeProps: [],
  });
  // prefetch app theme without triggering a re-render
  useQuery({
    queryKey: appThemeQueryOptions().queryKey,
    notifyOnChangeProps: [],
  });
  // prefetch user profile
  useQuery({
    queryKey: userProfileQueryOptions().queryKey,
    notifyOnChangeProps: [],
  });
  // prefetch the brandclub brand data
  useCurrentBrandclubProduct({ notifyOnChangeProps: [] });
  // prefetch the dtc site product (uses a regular query to ensure it's always re-fetched)
  useDtcSiteProduct({ notifyOnChangeProps: [] });
  // Use a regular query for prefetching active carts to ensure it's always re-fetched
  useQuery({
    ...activeCartsQueryOptions(),
    // This prevents unnecessary re-renders since we're just maintaining the query
    notifyOnChangeProps: [],
  });

  useEffect(() => {
    const fetchUser = () => {
      queryClient.fetchQuery(userProfileQueryOptions());
    };
    const ensureUserIsLoggedIn = () => {
      const cachedUserProfile = queryClient.getQueryData(
        userProfileQueryOptions().queryKey
      );
      if (!cachedUserProfile) {
        // avoids the scenario where we have a valid auth token but there is no user profile in the cache
        queryClient.fetchQuery(userProfileQueryOptions());
      }
    };
    const userSignedOut = () => {
      const cachedUserProfile = queryClient.getQueryData(
        userProfileQueryOptions().queryKey
      );
      if (cachedUserProfile) {
        persistedQueryManager.removeQuery({
          queryKey: userProfileQueryOptions().queryKey,
          type: "all",
        });
      }
    };
    window.addEventListener("bc-user-loggedout", userSignedOut);
    window.addEventListener("bc-user-refreshed", fetchUser);
    window.addEventListener("bc-user-loggedin", ensureUserIsLoggedIn);
    return () => {
      window.removeEventListener("bc-user-loggedout", userSignedOut);
      window.removeEventListener("bc-user-refreshed", fetchUser);
      window.removeEventListener("bc-user-loggedin", ensureUserIsLoggedIn);
    };
  });

  useEffect(() => {
    // invalidate queries on refresh
    const navigation = performance.getEntriesByType(
      "navigation"
    )[0] as PerformanceNavigationTiming;
    if (navigation?.type === "reload") {
      persistedQueryManager.invalidateQueries({
        queryKey: activeCartsQueryOptions().queryKey,
        type: "all",
      });
    }
  }, [persistedQueryManager]);

  return (
    <>
      <MixpanelTracking domain={HOST_DOMAIN} />
      {children}
    </>
  );
};
